'use client';
import { CalendarPlus01, Edit02, MessageTextSquare02, Plus, TypeSquare } from '@ethsign/icons';

import { AnnotationEditorType } from '@ethsign/pdf-editor';
import { Button } from '@ethsign/ui';
import { shortenWalletAddress } from '@ethsign/utils-web';
import classNames from 'classnames';
import { useEffect, useState } from 'react';

import { CheckGreenIcon, CircleCheckedIcon, CircleIcon, UserIcon } from '@/components';
import { ContractTypeCheckItem } from '@/components/ContractTypeCheckItem';
import { dragImage } from '@/constants/editor';
import { IContact, SendType, SIGN_MODE_ENUM } from '@/types';
import { isSameUser } from '@/utils';

interface Props {
  accounts: (IContact & { name?: string; address?: string } & any)[];
  annotations?: any[];
  sendType: SendType;
  onContractTypeChange?: (type: SIGN_MODE_ENUM) => void;
  contractType: SIGN_MODE_ENUM;
  isUseTemplate?: boolean;
  onAddEmptyPage?: () => void;
  onFieldClick?: (data: any) => void;
}

const colors = [
  { text: '#DC6803', bg: '#FCE4EC' },
  { text: '#D81B60', bg: '#FFFAEB' },
  { text: '#1570EF', bg: '#F5FBEE' },
  { text: '#175CD3', bg: '#EFF8FF' },
  { text: '#088AB2', bg: '#ECFDFF' },
  { text: '#764C97', bg: '#EBE5F0' },
  { text: '#475467', bg: '#F9FAFB' },
  { text: '#D92D20', bg: '#FEF3F2' }
];

const colorsLen = colors.length;

export function LefPanel(props: Props) {
  const { accounts, annotations, sendType, onContractTypeChange, contractType, isUseTemplate, onAddEmptyPage } = props;
  const [currentUser, setCurrentUser] = useState<any>(accounts[0] || {});
  const isInvite = sendType === SendType.invite;
  const index = isInvite ? (accounts.findIndex((item) => isSameUser(item, currentUser)) || 0) % colorsLen : 0;

  const dragStart = (e: any, type: keyof typeof dragImage) => {
    const container = document.createElement('div');
    container.id = 'dragimage-container';
    const img = dragImage[type].replaceAll('color', colors[index].text.slice(1));

    Object.assign(container.style, {
      opacity: '0.8',
      backgroundImage: `url("${img}")`,
      backgroundSize: '100% 100%',
      width: '94px',
      height: '26px'
    });
    document.body.appendChild(container);
    e.dataTransfer.setData(
      'data',
      JSON.stringify({
        bitmapFile: img,
        mode: type === 'readonlyText' ? AnnotationEditorType.FREETEXT : AnnotationEditorType.STAMP,
        width: type === 'readonlyText' ? undefined : 94 * 0.7,
        height: type === 'readonlyText' ? undefined : 26 * 0.7,
        data: {
          name: currentUser.name,
          address: currentUser.address,
          email: currentUser.email,
          color: colors[index].text.slice(1),
          type
        }
      })
    );
    e.dataTransfer.setDragImage(container, 0, 0);
  };

  const onFieldClick = (e: any, type: keyof typeof dragImage) => {
    const container = document.createElement('div');
    container.id = 'dragimage-container';
    const color = colors[index].text.slice(1);
    Object.assign(container.style, {
      opacity: '0.8',
      backgroundImage: `url("${dragImage[type].replaceAll('color', color)}")`,
      backgroundSize: '100% 100%',
      width: '94px',
      height: '26px',
      position: 'fixed',
      zIndex: '9999',
      transform: `translate3D(${e.clientX}px,${e.clientY}px,0)`,
      left: '0',
      top: '0'
    });
    props.onFieldClick?.({
      type,
      name: currentUser.name,
      address: currentUser.address,
      email: currentUser.email,
      color
    });
    document.body.appendChild(container);
  };
  useEffect(() => {
    sendType === SendType.link && onContractTypeChange?.(contractType);
  }, [contractType, sendType]);

  return (
    <div className="px-5 pt-8 h-full border-r border-gray-200 overflow-auto">
      <div className="text-lg font-bold text-gray-600">{isInvite ? 'Add Fields' : 'Settings'}</div>
      <div className="mt-6 mb-3 text-xs font-normal text-gray-400">
        {isInvite ? 'Assign Fields to:' : 'Contract Type:'}
      </div>
      <div className="max-h-[260px] overflow-auto">
        {isInvite ? (
          accounts.map((item, i) => {
            const active = isSameUser(item, currentUser);
            const hasSignature = annotations?.some(
              (it: any) => it.custom?.type === 'signature' && isSameUser(it.custom, item)
            );
            const isCompleted = hasSignature;
            const displayName =
              item.name || (item.address && shortenWalletAddress(item.address, 'shorter')) || item.email?.split('@')[0];
            return (
              <div
                key={i}
                className={`${
                  active ? 'bg-primary-50 border-primary-600' : ''
                } relative cursor-pointer mt-2 py-3 px-[14px] border border-gray-200 rounded-lg flex flex-row  justify-between items-center w-full`}
                onClick={() => {
                  setCurrentUser(item);
                }}
              >
                <span className="flex flex-1 flex-row items-center">
                  <UserIcon fill={colors[i % colorsLen].text} />
                  <span
                    className={classNames('flex-1 mx-2 text-md font-medium text-gray-500 truncate w-1', {
                      'text-primary-900': active
                    })}
                  >
                    {displayName}
                  </span>
                </span>
                <div className="">
                  {isCompleted ? <CheckGreenIcon /> : active ? <CircleCheckedIcon /> : <CircleIcon />}
                </div>
              </div>
            );
          })
        ) : (
          <div>
            {(isUseTemplate && contractType === SIGN_MODE_ENUM.OneSignOne) || !isUseTemplate ? (
              <ContractTypeCheckItem
                checked={contractType === SIGN_MODE_ENUM.OneSignOne}
                onClick={() => onContractTypeChange?.(SIGN_MODE_ENUM.OneSignOne)}
                title="Each signer signs their own contract"
                description="Add signature field to the contract. Everyone receives a separate copy"
              />
            ) : null}
            {(isUseTemplate && contractType === SIGN_MODE_ENUM.AllSignOne) || !isUseTemplate ? (
              <ContractTypeCheckItem
                className="mt-3"
                checked={contractType === SIGN_MODE_ENUM.AllSignOne}
                onClick={() => onContractTypeChange?.(SIGN_MODE_ENUM.AllSignOne)}
                title="All signatures on one contract"
                description="Signatures will be appended to the bottom of the contract. "
              />
            ) : null}
          </div>
        )}
      </div>
      <div className="mt-6 mb-3 text-xs font-normal text-gray-400">{isInvite ? 'Assign Fields:' : 'Add Fields:'}</div>
      <div className="mt-5 flex flex-row flex-wrap">
        {sendType === SendType.link && contractType === SIGN_MODE_ENUM.AllSignOne ? (
          <Button variant="primary" onClick={onAddEmptyPage}>
            <Plus size={16} color="#ffffff" />
            <span className="ml-2">Add Empty Page</span>
          </Button>
        ) : (
          <>
            <div
              onClick={(e) => onFieldClick(e, 'signature')}
              onDragStart={(e) => dragStart(e, 'signature')}
              draggable
              className="pt-[22px] py-[14px] overflow-hidden relative cursor-pointer hover:bg-yellow-25 w-[128px] h-[88px] mb-4 mr-4 flex flex-col justify-center items-center border border-gray-200 rounded-lg"
            >
              <Edit02 color="#475467" size={20} />
              <span className="mt-[14px] text-xs text-gray-700 font-medium">Signature Field</span>
              <span className="bg-tangerine-50 absolute right-0 top-0 text-xxs px-[6px] text-primary-800 rounded-bl-lg">
                Required
              </span>
            </div>
            <div
              draggable
              onClick={(e) => onFieldClick(e, 'date')}
              onDragStart={(e) => dragStart(e, 'date')}
              className="pt-[22px] py-[14px] overflow-hidden relative cursor-pointer hover:bg-yellow-25 w-[128px] h-[88px] mb-4 flex flex-col justify-center items-center border border-gray-200 rounded-lg"
            >
              <CalendarPlus01 color="#475467" size={20} />
              <span className="mt-[14px] text-xs text-gray-700 font-medium"> Date Signed</span>
            </div>
            <div
              onClick={(e) => onFieldClick(e, 'text')}
              onDragStart={(e) => dragStart(e, 'text')}
              draggable
              className="pt-[22px] py-[14px] overflow-hidden relative cursor-pointer hover:bg-yellow-25 w-[128px] h-[88px] flex flex-col justify-center items-center border border-gray-200 rounded-lg"
            >
              <TypeSquare color="#475467" size={20} />
              <span className="mt-[14px] text-xs text-gray-700 font-medium">Input Text Field</span>
            </div>
          </>
        )}
        {isInvite && (
          <>
            <div
              draggable
              onClick={(e) => onFieldClick(e, 'readonlyText')}
              onDragStart={(e) => dragStart(e, 'readonlyText')}
              className="ml-4 pt-[22px] py-[14px] overflow-hidden relative cursor-pointer hover:bg-yellow-25 w-[128px] h-[88px] flex flex-col justify-center items-center border border-gray-200 rounded-lg"
            >
              <MessageTextSquare02 color="#475467" size={20} />
              <span className="mt-[14px] text-xs text-gray-700 font-medium">Read Only Text</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
